import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { isPortalUpcomingAppointment } from '@/utils/appointmentEditability';
import {
  isPortalAppointmentPaid,
  usePortalAppointments,
  type PortalAppointmentItem,
} from '@/hooks/usePortalAppointments';
import { usePortalActiveClinic } from '@/lib/portalActiveClinic';
import { PortalBookAppointmentButton } from '@/components/portal/PortalBookAppointmentButton';
import { PortalPageHead } from '@/components/portal/PortalChrome';
import { PortalIcon, type PortalIconName } from '@/components/portal/PortalIcon';
import {
  PortalCancelModal,
  PortalRescheduleModal,
} from '@/components/portal/PortalAppointmentModals';
import {
  formatClinicAddress,
  formatPortalDateTime,
  formatPortalDateTimeRange,
  formatPortalMonthShort,
} from '@/lib/portalFormat';
import {
  appointmentRuleText,
  paymentChip,
  portalAppointmentHostLabel,
  statusChip,
} from '@/lib/portalAppointmentDisplay';
import { parseCourseInstant } from '@/lib/courseDateFormat';

export function PortalAppointments() {
  const { data: appointments = [], isLoading, error } = usePortalAppointments();
  const { clinics, activeClinicId } = usePortalActiveClinic();
  const clinic = clinics.find((item) => item.clinic_id === activeClinicId);
  const [selected, setSelected] = useState<{
    type: 'cancel' | 'reschedule';
    appointment: PortalAppointmentItem;
  } | null>(null);

  const { upcoming, past } = useMemo(() => {
    const now = new Date();
    const upcomingList: PortalAppointmentItem[] = [];
    const pastList: PortalAppointmentItem[] = [];
    appointments.forEach((apt) => {
      if (
        isPortalUpcomingAppointment({
          displayStatus: apt.display_status,
          startsAt: apt.starts_at,
          endsAt: apt.ends_at,
          now,
        })
      ) {
        upcomingList.push(apt);
      } else {
        pastList.push(apt);
      }
    });
    upcomingList.sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime());
    return { upcoming: upcomingList, past: pastList };
  }, [appointments]);

  return (
    <>
      <PortalPageHead
        title="Aftaler"
        subtitle={`Dine kommende og tidligere tider hos ${clinic?.clinic_name ?? 'klinikken'}.`}
        action={<PortalBookAppointmentButton />}
      />

      {isLoading ? (
        <section className="card">
          <p className="help">Henter aftaler…</p>
        </section>
      ) : error ? (
        <section className="card">
          <p className="help">Aftalerne kunne ikke hentes. Prøv igen.</p>
        </section>
      ) : (
        <>
          <AppointmentSection
            title="Kommende"
            items={upcoming}
            address={clinic ? formatClinicAddress(clinic.clinic_address) : ''}
            highlightFirst
            onCancel={(appointment) => setSelected({ type: 'cancel', appointment })}
            onReschedule={(appointment) => setSelected({ type: 'reschedule', appointment })}
          />
          <AppointmentSection
            title="Tidligere"
            items={past}
            address=""
            onCancel={(appointment) => setSelected({ type: 'cancel', appointment })}
            onReschedule={(appointment) => setSelected({ type: 'reschedule', appointment })}
          />
        </>
      )}

      {selected?.type === 'cancel' ? (
        <PortalCancelModal appointment={selected.appointment} onClose={() => setSelected(null)} />
      ) : null}
      {selected?.type === 'reschedule' ? (
        <PortalRescheduleModal appointment={selected.appointment} onClose={() => setSelected(null)} />
      ) : null}
    </>
  );
}

function AppointmentSection({
  title,
  items,
  address,
  highlightFirst,
  onCancel,
  onReschedule,
}: {
  title: string;
  items: PortalAppointmentItem[];
  address: string;
  highlightFirst?: boolean;
  onCancel: (appointment: PortalAppointmentItem) => void;
  onReschedule: (appointment: PortalAppointmentItem) => void;
}) {
  return (
    <section className="card" style={title === 'Tidligere' ? { marginTop: 24 } : undefined}>
      <div className="card-h">
        <h2>{title}</h2>
        <span className="count">{items.length}</span>
      </div>
      <div style={{ marginTop: 8 }}>
        {items.length === 0 ? (
          <p className="help">Ingen {title.toLowerCase()} tider.</p>
        ) : (
          items.map((appointment, index) => (
            <AppointmentRow
              key={appointment.id}
              appointment={appointment}
              address={address}
              highlight={Boolean(highlightFirst && index === 0)}
              onCancel={onCancel}
              onReschedule={onReschedule}
            />
          ))
        )}
      </div>
    </section>
  );
}

function AppointmentRow({
  appointment,
  address,
  highlight,
  onCancel,
  onReschedule,
}: {
  appointment: PortalAppointmentItem;
  address: string;
  highlight: boolean;
  onCancel: (appointment: PortalAppointmentItem) => void;
  onReschedule: (appointment: PortalAppointmentItem) => void;
}) {
  const parts = parseCourseInstant(appointment.starts_at);
  const status = statusChip(appointment.display_status);
  const payment = paymentChip(appointment);
  const paidLocked =
    appointment.display_status === 'upcoming' &&
    !appointment.rescheduling_allowed &&
    isPortalAppointmentPaid(appointment);
  const rule = appointmentRuleText(appointment, formatPortalDateTime(appointment.cancel_until));

  return (
    <div className="appt">
      <div className={`datebox${highlight ? ' on' : ''}`}>
        <div className="d num">{parts?.day ?? ''}</div>
        <div className="m">{appointment.starts_at ? formatPortalMonthShort(appointment.starts_at) : ''}</div>
      </div>
      <div className="grow">
        <div className="t num">{formatPortalDateTimeRange(appointment.starts_at, appointment.ends_at)}</div>
        <div className="s">{appointment.service_name ?? 'Aftale'}</div>
        <div className="s">
          {[portalAppointmentHostLabel(appointment, ''), address].filter(Boolean).join(' · ')}
        </div>
        <div className="chips">
          <Chip tone={status.tone} icon={status.icon} label={status.label} />
          <Chip tone={payment.tone} icon={payment.icon} label={payment.label} />
        </div>
        {rule ? <p className="rule">{rule}</p> : null}
      </div>
      <div className="acts">
        {appointment.video_meeting_url && appointment.display_status === 'upcoming' ? (
          <a
            className="btn btn-secondary"
            href={appointment.video_meeting_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <PortalIcon name="video" size="sm" />
            Deltag i videomøde
          </a>
        ) : null}
        {appointment.rescheduling_allowed ? (
          <button type="button" className="btn btn-secondary" onClick={() => onReschedule(appointment)}>
            <PortalIcon name="calendarMove" size="sm" />
            Flyt
          </button>
        ) : null}
        {appointment.cancellation_allowed ? (
          <button type="button" className="btn btn-secondary" onClick={() => onCancel(appointment)}>
            <PortalIcon name="calendarX" size="sm" />
            Aflys
          </button>
        ) : null}
        {paidLocked ? (
          <Link className="btn btn-secondary" to="/portal/messages">
            <PortalIcon name="message" size="sm" />
            Skriv til klinikken
          </Link>
        ) : null}
      </div>
    </div>
  );
}

function Chip({
  tone,
  icon,
  label,
}: {
  tone: 'teal' | 'green' | 'amber' | 'red' | 'neutral' | 'coral';
  icon: PortalIconName;
  label: string;
}) {
  return (
    <span className={`chip chip-${tone}`}>
      <PortalIcon name={icon} size="xs" />
      {label}
    </span>
  );
}
